import * as Sentry from '@sentry/react'
import sanitizeHtml from 'sanitize-html'
import { MarkdownProcessingError } from '@/content-viewer/errors/markdown-errors'
import { kSanitizationAllowedTags } from '@/constants/constants-ui'
import markdownToHtmlString from '@/util/markdown-parsing/markdown-to-html'
import { highlightPassages } from './highlight-passages'

/**
 * Convert a matter file's Markdown to sanitized HTML, keeping the start number of an ordered list
 * so a list that begins at 6 still shows 6.
 */
export async function convertReferenceMarkdownToHtml(markdown: string): Promise<string> {
  try {
    const rawHtml = await markdownToHtmlString(markdown)
    return sanitizeHtml(rawHtml, {
      allowedAttributes: { ...sanitizeHtml.defaults.allowedAttributes, ol: ['start'] },
    })
  } catch (error) {
    const processingError = new MarkdownProcessingError(`Failed to convert markdown to html string: ${(error as Error).message}`)
    Sentry.captureException(processingError, {
      extra: { error: JSON.stringify(error) },
    })
    throw processingError
  }
}

/** Sanitize a matter file's rendered HTML before highlighting, keeping ordered-list start numbers. */
function sanitizeReferenceHtml(html: string): string {
  return sanitizeHtml(html, {
    allowedTags: kSanitizationAllowedTags,
    allowedAttributes: {
      span: ['id', 'page-number', 'pagination-id'],
      ol: ['start'],
    },
    transformTags: {
      pre: 'p',
      code: 'p',
    },
  })
}

/** Sanitize a matter file's rendered HTML and highlight each passage once. */
export function highlightReferenceHtml(html: string, passages: string[]): string {
  const template = document.createElement('template')
  template.innerHTML = sanitizeReferenceHtml(html)
  if (passages.length > 0) highlightPassages(template.content, passages)
  return template.innerHTML
}
